import { useState } from "react"
import type { AuditEntry } from "../api/admin-client.ts"

/**
 * 감사 로그 타임라인 — 최신순 테이블.
 *
 * 가로가 좁아질 때 셀이 2줄로 줄바꿈되는 대신 **가로 스크롤** 되고, 각 행을 클릭하면
 * 아래에 **전체 diff 를 JSON pretty-print** 로 펼친다 (토글). Diff 셀 자체는 1줄 요약만
 * 보여주므로 스캔성과 상세 확인을 동시에 지원한다.
 */
export function AuditTimeline(props: { readonly entries: ReadonlyArray<AuditEntry> }) {
	const [expanded, setExpanded] = useState<Set<number>>(new Set())

	function toggle(id: number) {
		setExpanded((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	if (props.entries.length === 0) {
		return (
			<p className="muted" style={{ fontSize: 13 }}>
				감사 로그가 비어 있습니다.
			</p>
		)
	}

	return (
		<div className="audit-timeline">
			<div className="audit-timeline-scroll">
				<table className="audit-timeline-table">
					<thead>
						<tr>
							<th className="audit-timeline-col-toggle" aria-label="펼치기" />
							<th className="audit-timeline-col-at">시각</th>
							<th className="audit-timeline-col-actor">행위자</th>
							<th className="audit-timeline-col-action">작업</th>
							<th className="audit-timeline-col-target">대상</th>
							<th className="audit-timeline-col-diff">변경 내용</th>
						</tr>
					</thead>
					<tbody>
						{props.entries.map((entry) => {
							const isOpen = expanded.has(entry.id)
							const hasDiff = !!entry.diff && Object.keys(entry.diff).length > 0
							return (
								<AuditRow
									key={entry.id}
									entry={entry}
									open={isOpen}
									hasDiff={hasDiff}
									onToggle={() => toggle(entry.id)}
								/>
							)
						})}
					</tbody>
				</table>
			</div>
		</div>
	)
}

/**
 * 단일 audit 행 — 요약 tr + 펼침 시 diff tr 추가 렌더.
 *
 * 요약 tr 은 전체가 `onClick` 으로 토글. diff 가 없으면 caret 을 흐리게 하고 클릭해도
 * 아무 동작 안 함.
 */
function AuditRow(props: {
	readonly entry: AuditEntry
	readonly open: boolean
	readonly hasDiff: boolean
	readonly onToggle: () => void
}) {
	const { entry, open, hasDiff, onToggle } = props
	return (
		<>
			<tr
				className={`audit-timeline-row${hasDiff ? " is-interactive" : ""}${open ? " is-open" : ""}`}
				onClick={hasDiff ? onToggle : undefined}
				onKeyDown={(e) => {
					if (!hasDiff) return
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault()
						onToggle()
					}
				}}
				tabIndex={hasDiff ? 0 : undefined}
				aria-expanded={hasDiff ? open : undefined}
			>
				<td className="audit-timeline-col-toggle">
					{hasDiff ? (
						<span aria-hidden className="audit-timeline-caret">
							{open ? "▾" : "▸"}
						</span>
					) : (
						<span aria-hidden className="audit-timeline-caret is-muted">
							·
						</span>
					)}
				</td>
				<td className="audit-timeline-col-at muted">{new Date(entry.at).toLocaleString("ko-KR")}</td>
				<td className="audit-timeline-col-actor">{entry.actor}</td>
				<td className="audit-timeline-col-action">
					<code className="inline">{entry.action}</code>
				</td>
				<td className="audit-timeline-col-target muted">{entry.target ?? "—"}</td>
				<td className="audit-timeline-col-diff muted">{summarizeDiff(entry.diff)}</td>
			</tr>
			{open && hasDiff && (
				<tr className="audit-timeline-detail-row">
					<td />
					<td colSpan={5}>
						<pre className="audit-timeline-detail">{formatDiff(entry.diff)}</pre>
					</td>
				</tr>
			)}
		</>
	)
}

function summarizeDiff(diff: Record<string, unknown> | undefined): string {
	if (!diff || Object.keys(diff).length === 0) return "—"
	try {
		const json = JSON.stringify(diff)
		return json.length > 160 ? `${json.slice(0, 157)}...` : json
	} catch {
		return "(직렬화 불가)"
	}
}

function formatDiff(diff: Record<string, unknown> | undefined): string {
	if (!diff) return "(diff 없음)"
	try {
		return JSON.stringify(diff, null, 2)
	} catch {
		return "(직렬화 불가)"
	}
}
